'use client'

import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { testIds, testIdHelpers } from '@/root/shared-consts'
import { useForm, Controller, useFieldArray, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@/root/ui/shared/Button/Button'
import { MinusIcon } from '@/root/ui/shared/icons/MinusIcon'
import { PlusIcon } from '@/root/ui/shared/icons/PlusIcon'
import Loader from '@/root/ui/shared/Loader/Loader'
import { Spinner } from '@/root/ui/shared/Spinner/Spinner'
import { useAppConfig, useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import { Price } from '@/root/shared-types/dto/SailPricing'
import { LabelValue, PassengerCard, PassengerForm } from '@/root/modules/shared/SelectTicketPage'
import { createPriceCardSchema } from '@/root/modules/shared/SelectTicketPage/components/PriceCard/validation'
import { FormValues, PassengerFormData } from '@/root/shared-types/selectTicketPage/selectTicket'
import { CountryItem } from '@/root/shared-types'
import { createDefaultPassenger } from '@/root/modules/shared/SelectTicketPage/utils/passenger'
import { Banner } from '@/root/ui/shared/Banner/Banner'
import { FieldLevelError } from '@/root/shared-errors'
import { getTicketDescriptionKey } from './ticketType'
import { getPassengerFormVisibility } from '@/root/modules/shared/SelectTicketPage/helpers/passengerDataForm/passengerDataForm'
import type { ResidentOverrideCategory } from '@/root/modules/shared/SelectTicketPage/helpers/residentOverride/residentOverride'
import cn from 'classnames'

const STEP_SPINNER = 'size-5 border-2 border-white/40 border-t-white'
const TYPED_SPINNER = 'size-4 border-2 border-black/15 border-t-black/55'

/** Moves one resident ticket onto the opt-in category and back; answers with what it now is. */
export type ResidentOverrideToggle = (params: {
  personalId: string
  guestSeqN: number
  overrideCode: string
  checked: boolean
}) => Promise<{ priceCategory: string; guestSeqN: number } | null>

interface PriceCardProps {
  maxTicketQty: number
  price: Price
  countries?: CountryItem[]
  departureDate?: string
  disabled?: boolean
  hidePassengerDataForm?: boolean
  initialQuantity?: number
  initialSavedPassengers?: PassengerFormData[]
  onDec?: (price: Price) => void | Promise<void>
  onFormStateChange?: (priceCode: string, saved: number, total: number) => void
  onInc?: (price: Price) => void | Promise<void>
  onPassengerDeleted?: (
    price: Price,
    slotIndex: number,
    verifiedPriceCategory?: string,
    seqN?: number
  ) => void | Promise<void>
  onPassengerSaved?: (priceCode: string, slotIndex: number, data: PassengerFormData) => void | Promise<void>
  onResidentDec?: (price: Price) => void | Promise<void>
  onResidentIdVerified?: (
    price: Price,
    slotIndex: number,
    personalId: string,
    priceCategory: string
  ) => Promise<number | null>
  onResidentInc?: (price: Price) => void | Promise<void>
  onResidentOverrideToggled?: ResidentOverrideToggle
  onSetQuantity?: (price: Price, qty: number) => Promise<void>
  priceCategoryTitlesByCode?: Readonly<Record<string, string>>
  residentOverride?: ResidentOverrideCategory
  route?: string
  seqNs?: number[]
}

export const PriceCard: FC<PriceCardProps> = ({
  price,
  countries = [],
  route,
  departureDate,
  maxTicketQty,
  onInc,
  onDec,
  onResidentInc,
  onResidentDec,
  onResidentIdVerified,
  onResidentOverrideToggled,
  residentOverride,
  onPassengerSaved,
  onFormStateChange,
  onPassengerDeleted,
  onSetQuantity,
  priceCategoryTitlesByCode,
  disabled,
  hidePassengerDataForm = false,
  initialQuantity,
  initialSavedPassengers,
  seqNs,
}) => {
  const code = price.priceCategory.code
  const isResident = price.priceCategory.isResident
  const categoryTestId = price.priceCategory.type === 'CAR_DECK' ? 'iconBike' : code

  const { showForm, personalIdOnly } = getPassengerFormVisibility({
    priceCategoryType: price.priceCategory.type,
    isResident,
    hidePassengerDataForm,
  })

  const schema = useMemo(() => createPriceCardSchema({ [code]: maxTicketQty }), [code, maxTicketQty])

  const methods = useForm<FormValues>({
    defaultValues: {
      quantities: {},
      passengers: [],
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const { control, watch, setValue } = methods

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'passengers',
  })

  const [savedPassengers, setSavedPassengers] = useState<Record<string, PassengerFormData>>({})
  const [passengerInlineErrors, setPassengerInlineErrors] = useState<Record<string, string | undefined>>({})
  // Kept apart from the personal-code errors above: a refused toggle must not unconfirm a slot whose
  // ticket is still there, so it reports itself next to the checkbox instead.
  const [overrideErrors, setOverrideErrors] = useState<Record<string, string | undefined>>({})
  const [idVerifiedFields, setIdVerifiedFields] = useState<Set<string>>(new Set())
  const [isPassengersReady, setIsPassengersReady] = useState(!initialSavedPassengers?.length)
  const [pendingPassengerDelete, setPendingPassengerDelete] = useState<{
    fieldId: string
    trigger: 'card' | 'form'
  } | null>(null)
  const [pendingQuantityChange, setPendingQuantityChange] = useState<'inc' | 'dec' | 'typed' | null>(null)
  const savedPassengersRef = useRef(savedPassengers)

  const quantityInitRef = useRef(false)
  const passengersInitRef = useRef(false)
  const committedQtyRef = useRef(initialQuantity ?? 0)
  const isTypingRef = useRef(false)

  // Initialize quantity from reservation
  useEffect(() => {
    if (quantityInitRef.current || !initialQuantity) return
    quantityInitRef.current = true
    committedQtyRef.current = initialQuantity
    setValue(`quantities.${code}`, initialQuantity)
  }, [initialQuantity, code, setValue])

  useEffect(() => {
    if (passengersInitRef.current || !initialSavedPassengers?.length || fields.length === 0) return
    passengersInitRef.current = true
    setSavedPassengers(
      Object.fromEntries(
        fields
          .map((field, i) => [field.id, initialSavedPassengers[i]] as const)
          .filter(([, passenger]) => !!(passenger?.firstName || passenger?.lastName))
      )
    )
    setIdVerifiedFields((prev) => {
      const next = new Set(prev)
      fields.forEach((field, i) => {
        if (initialSavedPassengers[i]?.personalIdConfirmed) next.add(field.id)
      })
      return next
    })
    setIsPassengersReady(true)
    initialSavedPassengers.slice(0, fields.length).forEach((passenger, i) => {
      setValue(`passengers.${i}.firstName`, passenger.firstName)
      setValue(`passengers.${i}.lastName`, passenger.lastName)
      setValue(`passengers.${i}.birthdate`, passenger.birthdate)
      setValue(`passengers.${i}.citizenship`, passenger.citizenship ?? '')
      setValue(`passengers.${i}.personalId`, passenger.personalId ?? '')
      setValue(`passengers.${i}.additionalInfo`, passenger.additionalInfo ?? '')
      if (passenger.gender) {
        setValue(`passengers.${i}.gender`, passenger.gender)
      }
      setValue(`passengers.${i}.personalIdConfirmed`, passenger.personalIdConfirmed)
      if (passenger.priceCategory) {
        setValue(`passengers.${i}.priceCategory`, passenger.priceCategory)
      }
      if (passenger.seqN) {
        setValue(`passengers.${i}.seqN`, passenger.seqN)
      }
    })
  }, [fields, initialSavedPassengers, setValue])

  // Sync seqN from reservation to form fields (for all passenger types)
  useEffect(() => {
    if (!seqNs?.length) return
    seqNs.forEach((sn, i) => {
      if (i < fields.length && methods.getValues(`passengers.${i}.seqN`) !== sn) {
        setValue(`passengers.${i}.seqN`, sn)
      }
    })
  }, [seqNs, fields.length, setValue, methods])

  // Keep savedPassengersRef current so useEffect and dec() always see the latest value
  useEffect(() => {
    savedPassengersRef.current = savedPassengers
  })

  const { getTranslation } = useGetTranslations()
  const { locale } = useCustomRouter()
  const { formatMoney } = useAppConfig()

  const calPrice = price.calculatedPrice

  const quantities = watch('quantities', {})
  const quantity = Number(quantities[code] ?? 0)

  const total = quantity * calPrice

  // Single increment for the + button. Optimistic: updates UI before API call.
  const inc = async () => {
    const currentQty = Number(methods.getValues(`quantities.${code}`) ?? 0)
    const newQty = Math.min(maxTicketQty, currentQty + 1)
    setValue(`quantities.${code}`, newQty) // optimistic UI update
    setPendingQuantityChange('inc')
    try {
      if (isResident) {
        await onResidentInc?.(price)
      } else {
        await onInc?.(price)
      }
      committedQtyRef.current = newQty
    } catch {
      setValue(`quantities.${code}`, currentQty) // revert on failure
    } finally {
      setPendingQuantityChange(null)
    }
  }

  /** May the stepper drop this slot?
   *
   * While the passenger cards are shown, a saved ticket is removed through its own card — the stepper
   * only takes back what has not been saved yet. With the cards hidden there is no such card, so the
   * stepper is the ONLY way to remove a ticket and a saved slot may go too.
   *
   * An ID-verified (resident) slot is off limits either way: its removal belongs to the resident flow,
   * and the plain decrement handler is not even wired for resident categories. */
  const canDropSlot = (fieldId: string, saved: Record<string, PassengerFormData>): boolean =>
    !idVerifiedFields.has(fieldId) && (hidePassengerDataForm || !saved[fieldId])

  const findDroppableIndex = (saved: Record<string, PassengerFormData>): number => {
    for (let i = fields.length - 1; i >= 0; i--) {
      if (canDropSlot(fields[i].id, saved)) return i
    }
    return -1
  }

  // Single decrement for the - button. Optimistic.
  const dec = async () => {
    const unsavedIndex = findDroppableIndex(savedPassengersRef.current)
    if (unsavedIndex === -1) return

    const currentQty = quantity
    const newQty = currentQty - 1
    setValue(`quantities.${code}`, newQty) // optimistic UI update
    remove(unsavedIndex)
    setPendingQuantityChange('dec')
    try {
      if (isResident) {
        await onResidentDec?.(price)
      } else {
        await onDec?.(price)
      }
      committedQtyRef.current = newQty
    } catch {
      setValue(`quantities.${code}`, currentQty) // revert on failure
      append({ ...createDefaultPassenger(), priceCategory: code })
    } finally {
      setPendingQuantityChange(null)
    }
  }

  const lastUnsavedIndex = findDroppableIndex(savedPassengers)

  const bannerShownRef = useRef(!!(initialQuantity && initialQuantity > 0))
  const [bannerVisible, setBannerVisible] = useState(false)
  const showBanner = !(price.priceCategory.type === 'CAR_DECK')

  useEffect(() => {
    if (!showBanner) return
    if (!bannerShownRef.current && quantity >= 1) {
      bannerShownRef.current = true
      setBannerVisible(true)
    }
  }, [quantity, showBanner])

  // Clamp quantity when maxTicketQty decreases below current quantity (e.g. other tickets added)
  useEffect(() => {
    if (maxTicketQty && quantity > maxTicketQty && !isTypingRef.current) {
      const clamped = Math.max(0, maxTicketQty)
      setValue(`quantities.${code}`, clamped)
      committedQtyRef.current = clamped
    }
  }, [maxTicketQty, quantity, setValue, code])

  // Sync fields array with quantity. When quantity decreases, only removes unsaved passengers.
  // If saved passengers block reaching the target, snaps quantity back to the minimum reachable.
  useEffect(() => {
    if (quantity > fields.length && !disabled) {
      append(
        Array.from({ length: quantity - fields.length }).map(() => ({
          ...createDefaultPassenger(),
          priceCategory: code,
        }))
      )
    }

    if (quantity < fields.length) {
      const saved = savedPassengersRef.current
      const needed = fields.length - quantity
      const isRemovable = (fieldId: string) =>
        hidePassengerDataForm ? !idVerifiedFields.has(fieldId) : !saved[fieldId]
      const toRemove = fields
        .map((field, i) => (isRemovable(field.id) ? i : -1))
        .filter((i) => i !== -1)
        .slice(-needed)
      if (toRemove.length > 0) remove(toRemove)
      // Snap quantity if saved passengers prevent reaching the target
      // Don't snap while the user is actively typing — let them finish first
      if (toRemove.length < needed && !isTypingRef.current) {
        setValue(`quantities.${code}`, fields.length - toRemove.length)
      }
    }
  }, [quantity, fields, append, remove, code, setValue, disabled, hidePassengerDataForm, idVerifiedFields])

  const handleDeletePassenger = async (index: number, fieldId: string, trigger: 'card' | 'form') => {
    setPendingPassengerDelete({ fieldId, trigger })
    try {
      const newQty = Math.max(0, quantity - 1)
      const isConfirmed =
        savedPassengers[fieldId]?.personalIdConfirmed ?? methods.getValues(`passengers.${index}.personalIdConfirmed`)
      const verifiedPC = isConfirmed
        ? (savedPassengers[fieldId]?.priceCategory ?? methods.getValues(`passengers.${index}.priceCategory`)) ||
          undefined
        : undefined
      const seqN: number | undefined =
        savedPassengers[fieldId]?.seqN ?? methods.getValues(`passengers.${index}.seqN`) ?? undefined
      await onPassengerDeleted?.(price, index, verifiedPC, seqN)
      remove(index)
      setSavedPassengers((prev) => {
        const copy = { ...prev }
        delete copy[fieldId]
        return copy
      })
      setIdVerifiedFields((prev) => {
        const next = new Set(prev)
        next.delete(fieldId)
        return next
      })
      committedQtyRef.current = newQty
      setValue(`quantities.${code}`, newQty)
    } finally {
      setPendingPassengerDelete(null)
    }
  }

  const handleResidentIdVerifiedForField = async (
    fieldId: string,
    index: number,
    personalId: string,
    priceCategory: string
  ): Promise<boolean> => {
    try {
      let seqN: number | undefined
      if (onResidentIdVerified) {
        const appliedSeqN = await onResidentIdVerified(price, index, personalId, priceCategory)
        if (appliedSeqN === null) return false
        seqN = appliedSeqN
      }
      setIdVerifiedFields((prev) => new Set(prev).add(fieldId))
      if (seqN !== undefined) {
        setValue(`passengers.${index}.seqN`, seqN)
      }
      return true
    } catch (e) {
      if (e instanceof FieldLevelError) {
        setPassengerInlineErrors((prev) => ({ ...prev, [fieldId]: e.message }))
        setOpenPassengers((prev) => ({ ...prev, [fieldId]: true }))
      }
      throw e
    }
  }

  /** The swap replaces the guest, so the slot takes the category and seqN the flow answers with. */
  const handleResidentOverrideToggle = async (fieldId: string, index: number, checked: boolean): Promise<void> => {
    if (!onResidentOverrideToggled || !residentOverride) return
    const personalId = methods.getValues(`passengers.${index}.personalId`) ?? ''
    const guestSeqN = methods.getValues(`passengers.${index}.seqN`)
    if (!personalId || !guestSeqN) return

    setOverrideErrors((prev) => ({ ...prev, [fieldId]: undefined }))

    try {
      const result = await onResidentOverrideToggled({
        personalId,
        guestSeqN,
        overrideCode: residentOverride.code,
        checked,
      })
      if (result === null) return
      const { priceCategory, guestSeqN: newGuestSeqN } = result
      setValue(`passengers.${index}.priceCategory`, priceCategory)
      if (newGuestSeqN) setValue(`passengers.${index}.seqN`, newGuestSeqN)
      setSavedPassengers((prev) =>
        prev[fieldId]
          ? { ...prev, [fieldId]: { ...prev[fieldId], priceCategory, seqN: newGuestSeqN || prev[fieldId].seqN } }
          : prev
      )
    } catch (e) {
      if (e instanceof FieldLevelError) {
        setOverrideErrors((prev) => ({ ...prev, [fieldId]: e.message }))
      }
      throw e
    }
  }

  const handleSavePassenger = async (fieldId: string, index: number, data: PassengerFormData) => {
    setPassengerInlineErrors((prev) => ({ ...prev, [fieldId]: undefined }))
    const seqN = data.seqN ?? methods.getValues(`passengers.${index}.seqN`)
    const dataWithSeqN = { ...data, seqN }
    const key = seqN ?? index
    if (isResident && data.personalIdConfirmed) {
      const residentCode = data.priceCategory || code
      await onPassengerSaved?.(residentCode, key, dataWithSeqN)
    } else if (!isResident) {
      await onPassengerSaved?.(code, key, dataWithSeqN)
    }
    setSavedPassengers((prev) => ({
      ...prev,
      [fieldId]: { ...data },
    }))
    setIdVerifiedFields((prev) => {
      const next = new Set(prev)
      next.delete(fieldId)
      return next
    })
  }

  useEffect(() => {
    if (!showBanner) return
    if (hidePassengerDataForm) {
      const savedCount = personalIdOnly ? idVerifiedFields.size : fields.length
      onFormStateChange?.(code, savedCount, fields.length)
      return
    }
    onFormStateChange?.(code, Object.keys(savedPassengers).length, fields.length)
  }, [
    savedPassengers,
    fields.length,
    code,
    onFormStateChange,
    showBanner,
    hidePassengerDataForm,
    personalIdOnly,
    idVerifiedFields,
  ])

  const [openPassengers, setOpenPassengers] = useState<Record<string, boolean>>({})

  const togglePassenger = (fieldId: string) => {
    setOpenPassengers((prev) => ({
      ...prev,
      [fieldId]: !prev[fieldId],
    }))
  }

  const title = isResident ? getTranslation('selectTicketsResidentTicket') : price.priceCategory.titles[locale]
  const description = isResident
    ? getTranslation('selectTicketsResidentDescription')
    : getTranslation(getTicketDescriptionKey(price.priceCategory))

  const isStepSpinnerVisible = hidePassengerDataForm && !showForm
  const isIncPending = isStepSpinnerVisible && pendingQuantityChange === 'inc'
  const isDecPending = isStepSpinnerVisible && pendingQuantityChange === 'dec'
  const isTypedPending = isStepSpinnerVisible && pendingQuantityChange === 'typed'

  const tariff = isResident ? '-' : formatMoney(calPrice)
  const totalPrice = isResident ? '-' : formatMoney(total)
  const priceCN = cn('mt-1 text-card-value', isResident ? 'text-5xl leading-5' : 'text-ticket-amount')
  const subtotalCN = cn('mt-1 text-card-subtotal', isResident ? 'text-5xl leading-5' : 'text-ticket-subtotal')

  return (
    <FormProvider {...methods}>
      <div
        data-testid={testIdHelpers.category(categoryTestId).card}
        className="flex w-full flex-col rounded-2xl bg-white p-5"
      >
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <h2 data-testid={testIds.tickets.categoryTitle} className="text-card-title text-ticket-title">
              {title}
            </h2>
            <p data-testid={testIds.tickets.categoryDescription} className="text-card-desc text-ticket-desc">
              {description}
            </p>
          </div>

          <div className="grid w-full grid-cols-[1fr_auto_1fr] gap-4">
            <LabelValue
              label={getTranslation('selectTicketsPrice')}
              ddClassName={priceCN}
              labelClassName="text-card-label"
            >
              <span data-testid={testIds.tickets.categoryPrice}>{tariff}</span>
            </LabelValue>

            <LabelValue
              label={getTranslation('selectTicketsQty')}
              ddClassName="flex items-center gap-3"
              labelClassName="text-card-label"
            >
              <div data-testid={testIds.tickets.buttonsIncDec} className="flex items-center gap-3">
                <Button
                  variant="square"
                  onClick={() => dec()}
                  disabled={disabled || lastUnsavedIndex === -1}
                  aria-busy={isDecPending}
                  aria-label={getTranslation('selectTicketsRemoveOne', { title })}
                  data-testid={testIdHelpers.category(categoryTestId).minus}
                >
                  {isDecPending ? <Spinner className={STEP_SPINNER} /> : <MinusIcon width={20} height={20} />}
                </Button>

                <Controller
                  name={`quantities.${code}`}
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <span className="relative inline-flex">
                      {isTypedPending && (
                        <span className="absolute inset-px z-10 flex items-center justify-center rounded-lg bg-white">
                          <Spinner className={TYPED_SPINNER} />
                        </span>
                      )}
                      <input
                        {...field}
                        data-testid={testIdHelpers.category(categoryTestId).input}
                        type="number"
                        min={0}
                        max={maxTicketQty}
                        disabled={disabled}
                        aria-busy={isTypedPending}
                        onChange={(e) => {
                          const value = e.target.value
                          const parsedValue = value === '' ? '' : Number(value)
                          isTypingRef.current = true
                          if (parsedValue === '' || (parsedValue >= 0 && parsedValue <= maxTicketQty)) {
                            field.onChange(parsedValue)
                          }
                        }}
                        onBlur={async () => {
                          isTypingRef.current = false
                          const savedCount = Object.keys(savedPassengersRef.current).length
                          const rawValue = field.value === '' ? 0 : Number(field.value)
                          const effectiveTarget = Math.max(savedCount, Math.min(rawValue, maxTicketQty))
                          field.onChange(effectiveTarget)
                          const delta = effectiveTarget - committedQtyRef.current
                          if (delta !== 0 && onSetQuantity) {
                            setPendingQuantityChange('typed')
                            try {
                              await onSetQuantity(price, effectiveTarget)
                              committedQtyRef.current = effectiveTarget
                            } catch {
                              field.onChange(committedQtyRef.current)
                            } finally {
                              setPendingQuantityChange(null)
                            }
                          }
                        }}
                        className="border-neutral-medium-lighter text-card-quantity text-ticket-qty h-9 w-9 rounded-lg border p-2 text-center shadow-sm"
                      />
                    </span>
                  )}
                />

                <Button
                  variant="square"
                  onClick={() => inc()}
                  disabled={disabled || quantity >= maxTicketQty}
                  aria-busy={isIncPending}
                  aria-label={getTranslation('selectTicketsAddOne', { title })}
                  data-testid={testIdHelpers.category(categoryTestId).plus}
                >
                  {isIncPending ? <Spinner className={STEP_SPINNER} /> : <PlusIcon width={20} height={20} />}
                </Button>
              </div>
            </LabelValue>
            <LabelValue
              label={getTranslation('selectTicketsSubtotal')}
              ddClassName={subtotalCN}
              labelClassName="text-card-label"
            >
              <span data-testid={testIds.tickets.categorySubtotal}>{totalPrice}</span>
            </LabelValue>
          </div>
        </div>

        {showForm && (
          <>
            {bannerVisible && (
              <Banner
                className="my-5"
                variant="warning"
                onClose={() => setBannerVisible(false)}
                closeLabel={getTranslation('bannerClose')}
                body={getTranslation('selectTicketsWarningArea')}
                bodyClassName="text-left"
              />
            )}
            {(() => {
              const cards = fields.map((field, index) => ({
                index,
                node: (
                  <div key={field.id} className="relative" style={{ order: index }}>
                    <PassengerCard
                      variant={
                        savedPassengers[field.id] || (personalIdOnly && idVerifiedFields.has(field.id))
                          ? 'success'
                          : 'error'
                      }
                      firstName={savedPassengers[field.id]?.firstName}
                      lastName={savedPassengers[field.id]?.lastName}
                      personalId={
                        savedPassengers[field.id]?.personalId ??
                        (personalIdOnly && idVerifiedFields.has(field.id)
                          ? watch(`passengers.${index}.personalId`)
                          : undefined)
                      }
                      isResident={isResident}
                      personalIdOnly={personalIdOnly}
                      isOpen={!!openPassengers[field.id]}
                      onToggle={() => togglePassenger(field.id)}
                      onDelete={() => handleDeletePassenger(index, field.id, 'card')}
                      isDeleting={
                        pendingPassengerDelete?.fieldId === field.id && pendingPassengerDelete.trigger === 'card'
                      }
                      disabled={disabled || pendingPassengerDelete?.fieldId === field.id}
                    >
                      <PassengerForm
                        index={index}
                        onSave={(data) => handleSavePassenger(field.id, index, data)}
                        onDelete={() => handleDeletePassenger(index, field.id, 'form')}
                        onResidentIdVerified={(pid, pc) => handleResidentIdVerifiedForField(field.id, index, pid, pc)}
                        countries={countries}
                        isResident={isResident}
                        passenger={savedPassengers[field.id]}
                        route={route}
                        departureDate={departureDate}
                        disabled={disabled || pendingPassengerDelete?.fieldId === field.id}
                        onClose={() => togglePassenger(field.id)}
                        personalIdError={passengerInlineErrors[field.id]}
                        personalIdOnly={personalIdOnly}
                        priceCategoryTitle={priceCategoryTitlesByCode?.[watch(`passengers.${index}.priceCategory`)]}
                        residentOverride={
                          isResident && residentOverride && onResidentOverrideToggled
                            ? {
                                ...residentOverride,
                                error: overrideErrors[field.id],
                                onToggle: (checked) => handleResidentOverrideToggle(field.id, index, checked),
                              }
                            : undefined
                        }
                      />
                    </PassengerCard>
                  </div>
                ),
              }))

              const loaders =
                quantity > fields.length && disabled
                  ? Array.from({ length: quantity - fields.length }).map((_, i) => {
                      const index = fields.length + i
                      return {
                        index,
                        node: (
                          // oxlint-disable-next-line react/no-array-index-key
                          <div
                            key={`loading-${i}`}
                            className="flex h-12 items-center justify-center rounded-2xl bg-neutral-100"
                            style={{ order: index }}
                          >
                            <Loader className="size-8" />
                          </div>
                        ),
                      }
                    })
                  : []

              // Masonry: two independently packed columns on md+, single ordered column below.
              // Round-robin by index keeps each card's column stable when items are added/removed;
              // CSS `order` (set per item) restores natural 1-2-3 order in the single mobile column.
              const items = [...cards, ...loaders]
              const columns = [
                items.filter((item) => item.index % 2 === 0),
                items.filter((item) => item.index % 2 === 1),
              ]

              return (
                <div
                  data-testid={testIds.tickets.passengers}
                  className={cn(
                    'flex flex-col gap-5 md:flex-row md:items-start',
                    fields.length && 'mt-5',
                    !isPassengersReady && 'invisible'
                  )}
                >
                  {columns.map((columnItems, col) => (
                    <div
                      key={col === 0 ? 'col-left' : 'col-right'}
                      className="contents md:flex md:flex-1 md:flex-col md:gap-5"
                    >
                      {columnItems.map((item) => item.node)}
                    </div>
                  ))}
                </div>
              )
            })()}
          </>
        )}
      </div>
    </FormProvider>
  )
}
